import type { ReactNode } from "react";
import { Tabs, type Tab } from "@/components/ui/tabs/Tabs";
import { Avatar } from "@/components/ui/Avatar";
import { useNavigate } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { generateGradient } from "../utils/gradientGenerator";
import { hashString } from "../utils/hashString";
import styles from "./PageLayout.module.css";
import { Button, type MenuItem } from "@/components/ui/button/Button";
import { useIsOsMode, useWindowId } from "@/components/ui/os-layout";
import {
  WORKBENCH_ROOT_TEXT_CLASS,
  WORKBENCH_SURFACE_CLASS,
} from "@/components/ui/workbench";

export interface PageLayoutProps {
  title: string;
  icon?: ReactNode;
  children?: ReactNode;
  tabs?: Tab[];
  activeTab?: string;
  fullTitle?: boolean;
  onTabChange?: (tabId: string) => void;
  primaryAction?:
    | {
        label: string;
        onClick: () => void;
        icon?: ReactNode;
        dropdownItems?: MenuItem[];
      }
    | ReactNode;
  tabAction?: {
    icon: ReactNode;
    onClick: () => void;
    label: string;
  };
  staticTabs?: boolean;
  allowEdit?: boolean;
  /**
   * Disables icon editing functionality for tabs. When true, users will not be able
   * to click on tab icons to open the icon/color picker. Useful when you want tabs
   * to be clickable but not customizable.
   */
  disableIconEdit?: boolean;
  onRemoveTab?: (tabId: string) => void;
  avatar?: {
    seed?: string;
    size?: "sm" | "md" | "lg" | "xl" | "xs";
  };
  subtitle?: string;
  status?: {
    color: string;
    text: string;
  };
  backLink?: {
    to: string;
    label?: string;
  };
  columns?: boolean;
  hideHeader?: boolean;
  onEditModeChange?: (isEditMode: boolean) => void;
  gradientName?: string;
  backgroundColor?: string;
  subtitleComponent?: ReactNode;
  headerActionsComponent?: ReactNode;
  headerDetailsComponent?: ReactNode;
  /** ReactNode to render in the right side of the tabs header (e.g., actions for OS mode) */
  tabRightSlot?: ReactNode;
  /** Let Floating Ani switch tabs and reach targets inside them (see `Tabs` `aniNav`). */
  aniNav?: boolean;
}

export function PageLayout({
  title,
  icon,
  children,
  tabs,
  activeTab,
  onTabChange,
  primaryAction,
  tabAction,
  staticTabs,
  allowEdit,
  disableIconEdit,
  onRemoveTab,
  avatar,
  subtitle,
  status,
  backLink,
  columns = false,
  hideHeader = false,
  onEditModeChange,
  gradientName,
  backgroundColor,
  subtitleComponent,
  headerActionsComponent,
  headerDetailsComponent,
  fullTitle,
  tabRightSlot,
  aniNav,
}: PageLayoutProps) {
  // Get the content for the active tab (or null if tabs are empty)
  const hasTabsContent = tabs && tabs.length > 0;
  const navigate = useNavigate();
  /** True inside {@link OsProvider} (Ani OS shell). Prefer over actions context so the page header hides whenever OS chrome is active — the window title bar already shows the title. */
  /** True in aniOS shell or inside an OS window — hide duplicate page title / classic header row. */
  const isOsMode = useIsOsMode();
  const osWindowId = useWindowId();
  const compactPageHeader = isOsMode || osWindowId != null;

  const osSubtitleLeft =
    compactPageHeader && subtitleComponent ? (
      <div className="flex min-w-0 flex-1 items-center justify-start pr-2">
        {subtitleComponent}
      </div>
    ) : null;

  const handleBackClick = () => {
    if (!backLink) return;
    // Classic in-app navigation only. OS mode does not show this control: `useOsNavigate`
    // would open a second window (e.g. duplicate screener) while the original stays open.
    navigate(backLink.to);
  };

  const hasHeaderActions = Boolean(
    headerActionsComponent || headerDetailsComponent || primaryAction,
  );
  const showHeaderRow =
    !hideHeader &&
    (!compactPageHeader || Boolean(osSubtitleLeft) || hasHeaderActions);

  return (
    <div className={`flex relative flex-col h-full ${backgroundColor || ""}`}>
      {/* Background gradient - positioned under content */}
      {!backgroundColor && gradientName && (
        <div
          key={gradientName}
          className={styles.gradientBackground}
          style={
            {
              background: generateGradient(gradientName),
              filter: `blur(${
                60 + Math.floor(hashString(gradientName) * 40)
              }px)`,
              "--x-offset": `${Math.floor(hashString(gradientName) * 60)}%`,
              "--final-opacity": `${
                0.4 +
                hashString(gradientName.split("").reverse().join("")) * 0.3
              }`,
              left: `${Math.floor(hashString(gradientName) * 40)}%`,
              width: `${
                700 +
                Math.floor(
                  hashString(gradientName.split("").reverse().join("")) * 200
                )
              }px`,
              height: `${
                400 +
                Math.floor(
                  hashString(gradientName.split("").reverse().join("")) * 200
                )
              }px`,
            } as React.CSSProperties
          }
        />
      )}

      {/* Header */}
      {showHeaderRow && (
        <div
          className={`relative z-[10000] flex items-center ${
            compactPageHeader
              ? osSubtitleLeft
                ? "p-2 px-4 justify-between"
                : "p-2 px-4 justify-end"
              : "p-4 pl-6 pb-0"
          }`}
        >
          {/* Title section — hidden in aniOS shell or OS window (chrome / title bar already shows title) */}
          {!compactPageHeader && (
            <div className="flex items-center flex-grow">
              {/* Back Link */}
              {backLink && (
                <button
                  onClick={handleBackClick}
                  className=" mr-6 cursor-pointer flex items-center text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-gray-300 transition-colors mr-3"
                  aria-label="Go back"
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                </button>
              )}
              {/* Avatar or Icon (if provided) */}
              {icon ? (
                <div className="mr-4">{icon}</div>
              ) : (
                avatar && (
                  <div className="relative">
                    <Avatar
                      seed={avatar.seed || ""}
                      size={avatar.size || "md"}
                      className="mr-4"
                    />
                    {status && (
                      <div className="flex items-center absolute bottom-0 right-2">
                        <div
                          className={`size-2.5 ring-white dark:ring-slate-800 ring-2 flex-shrink-0 ring-offset-0.5 rounded-full mr-2 ${status.color}`}
                        ></div>
                      </div>
                    )}
                  </div>
                )
              )}

              <div className="flex flex-col flex-grow leading-none">
                <h1 className="text-xl leading-none min-h-[28px] flex items-center font-semibold text-slate-900 dark:text-white mb-0">
                  <div className="flex flex-row gap-2 w-full items-center">
                    <div className={`flex ${fullTitle ? "flex-grow" : ""}`}>
                      {title}
                    </div>
                    {headerActionsComponent && (
                      <div className="flex flex-row gap-2 items-center">
                        {headerActionsComponent}
                      </div>
                    )}
                  </div>
                </h1>
                {/* Subtitle and status container */}
                {(subtitle || status || subtitleComponent) && (
                  <div className="flex flex-col gap-y-1 min-h-[14px] ">
                    <div className="flex items-center gap-x-2">
                      {subtitle && (
                        <span className="text-sm leading-none font-mono text-gray-500 dark:text-gray-400">
                          {subtitle}
                        </span>
                      )}
                      {status && (
                        <div className="flex items-center leading-none font-mono">
                          <div
                            className={`w-2 h-2 rounded-full ${status.color} mr-1`}
                          ></div>
                          <div className="text-xs tracking-widest text-gray-500 dark:text-gray-400">
                            {status.text}
                          </div>
                        </div>
                      )}
                    </div>
                    {subtitleComponent && (
                      <div className="">{subtitleComponent}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {osSubtitleLeft}

          {/* Action buttons - visible in both OS and Classic modes */}
          {(headerActionsComponent ||
            headerDetailsComponent ||
            primaryAction) && (
            <div className="flex shrink-0 items-center gap-2">
              {/* In OS shell or OS window, show headerActionsComponent next to primary actions */}
              {compactPageHeader && headerActionsComponent && (
                <div className="flex flex-row gap-2 items-center">
                  {headerActionsComponent}
                </div>
              )}
              {headerDetailsComponent && (
                <div className="flex flex-row gap-2 items-center">
                  {headerDetailsComponent}
                </div>
              )}
              {primaryAction && (
                <div className="flex items-center space-x-2">
                  {typeof primaryAction === "object" &&
                  "onClick" in primaryAction ? (
                    primaryAction.dropdownItems ? (
                      <Button
                        onClick={primaryAction.onClick}
                        dropdown={true}
                        dropdownItems={primaryAction.dropdownItems}
                        color="indigo"
                      >
                        {primaryAction.icon && (
                          <span data-slot="icon">{primaryAction.icon}</span>
                        )}
                        {primaryAction.label}
                      </Button>
                    ) : (
                      <Button onClick={primaryAction.onClick} color="indigo">
                        {primaryAction.icon && (
                          <span data-slot="icon">{primaryAction.icon}</span>
                        )}
                        {primaryAction.label}
                      </Button>
                    )
                  ) : (
                    primaryAction
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Content Area */}
      <div
        className={`flex-1 ${columns ? "overflow-hidden" : "overflow-auto"}`}
      >
        {hasTabsContent ? (
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={onTabChange}
            actionButton={tabAction}
            rightSlot={tabRightSlot}
            allowEdit={allowEdit}
            disableIconEdit={disableIconEdit}
            onRemoveTab={onRemoveTab}
            scrollable={true}
            onEditModeChange={onEditModeChange}
            staticTabs={staticTabs}
            aniNav={aniNav}
          />
        ) : (
          <div className="p-4 h-full">
            <div
              className={`h-full w-full rounded-2xl overflow-y-auto ${WORKBENCH_SURFACE_CLASS} ${WORKBENCH_ROOT_TEXT_CLASS} ${
                columns ? "" : "p-4"
              }`}
            >
              {children}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
